import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TutorProfileEdit.css';

const TutorProfileEdit = () => {
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [bioText, setBioText] = useState('');
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('No changes saved yet');
  const [statusColor, setStatusColor] = useState('');

  const [selectedDays, setSelectedDays] = useState({
    monday: false, tuesday: false, wednesday: false,
    thursday: false, friday: false, saturday: false, sunday: false
  });

  const MAX_BIO_CHARS = 500;
  const totalLikes = 0; // Constant

  // Fetch profile on load
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('https://mykaddle-api.onrender.com/api/tutor/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(res.data);

        if (res.data.bio) {
          setBioText(res.data.bio);
        }

        if (res.data.availability && Array.isArray(res.data.availability)) {
          const newDays = {
            monday: false, tuesday: false, wednesday: false,
            thursday: false, friday: false, saturday: false, sunday: false
          };
          res.data.availability.forEach(day => {
            const dayKey = day.toLowerCase();
            if (newDays.hasOwnProperty(dayKey)) {
              newDays[dayKey] = true;
            }
          });
          setSelectedDays(newDays);
        }
      } catch (err) {
        console.error("Error fetching profile", err);
      }
    };
    fetchProfile();
  }, []);

  const handleGoBack = () => navigate(-1);

  // ── BIO ──
  const openBioModal = () => {
    setBioText(profile?.bio || '');
    setIsBioModalOpen(true);
  };

  const closeBioModal = () => setIsBioModalOpen(false);

  const saveBio = async () => {
    if (bioText.trim().length === 0) {
      alert('Bio cannot be empty.');
      return;
    }
    if (bioText.length > MAX_BIO_CHARS) {
      alert('Bio must be 500 characters or less.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        'https://mykaddle-api.onrender.com/api/tutor/bio',
        { bio: bioText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(prev => ({ ...prev, bio: bioText }));
      closeBioModal();
    } catch (err) {
      console.error(err);
      alert('Failed to save bio');
    }
  };

  // ── SHARE MODAL ──
  const openShareModal = () => setIsShareModalOpen(true);
  const closeShareModal = () => setIsShareModalOpen(false);

  const copyProfileLink = () => {
    const linkText = `https://mykaddle.com/${profile?.slug || 'tutor'}`;
    navigator.clipboard.writeText(linkText).then(() => {
      alert('Link copied!');
      closeShareModal();
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = linkText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied!');
      closeShareModal();
    });
  };

  // ── AVAILABILITY ──
  const handleDayClick = (day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const saveAvailability = async () => {
    const daysArray = Object.entries(selectedDays)
      .filter(([_, isSelected]) => isSelected)
      .map(([day]) => day);

    if (daysArray.length === 0) {
      setStatusMessage('Please select at least one day before saving');
      setStatusColor('#d64045');
      setTimeout(() => {
        setStatusColor('');
        setStatusMessage('No changes saved yet');
      }, 2000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        'https://mykaddle-api.onrender.com/api/tutor/availability',
        { availability: daysArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatusMessage(`Availability updated for: ${daysArray.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}`);
      setStatusColor('#8a8b14');
      setTimeout(() => {
        setStatusColor('');
        setStatusMessage('No changes saved yet');
      }, 3000);

      localStorage.setItem('tutorAvailability', JSON.stringify(daysArray));
    } catch (err) {
      console.error(err);
      alert('Failed to save availability');
    }
  };

  // ── PHOTO UPLOAD ──
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        'https://mykaddle-api.onrender.com/api/tutor/upload-photo',
        formData,
        { headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        } }
      );
      setProfile(prev => ({
        ...prev,
        profilePic: {
          ...prev.profilePic,
          url: res.data.url,
          cloudinaryId: res.data.cloudinaryId
        }
      }));
      setUploading(false);
    } catch (err) {
      console.error(err);
      alert('Upload failed');
      setUploading(false);
    }
  };

  // Escape key for modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isBioModalOpen) closeBioModal();
        if (isShareModalOpen) closeShareModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isBioModalOpen, isShareModalOpen]);

  const getDisplayName = () => {
    if (!profile) return 'Loading...';
    const firstName = profile.firstName || profile.user?.firstName || '';
    const lastName = profile.lastName || profile.user?.lastName || '';
    const title = profile.title || '';
    return `${title} ${firstName} ${lastName}`.trim();
  };

  const getQualification = () => {
    return profile?.education?.[0]?.qualification || 'BSc Mathematical Statistics';
  };

  const getInstitution = () => {
    return profile?.education?.[0]?.institution || 'Stellenbosch University';
  };

  const getBioCharCount = () => bioText.length;

  return (
    <div className="tpe-dashboard-container">
      <div className="tpe-almighty-wrapper">

        {/* TOP BAR */}
        <div className="tpe-top-bar">
          <div className="tpe-top-left-header">
            <div className="tpe-header-row">
              <button className="tpe-back-icon-btn" aria-label="Go back" onClick={handleGoBack}>
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none"/>
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z"/>
                </svg>
              </button>
              <span className="tpe-page-title">Profile</span>
            </div>
            <p className="tpe-sub-header">Manage your profile information so students can learn about you and your qualifications.</p>
          </div>
          <button className="tpe-top-right-icon" aria-label="Share profile link" onClick={openShareModal}>
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16">
              <path d="M0 0h16v16H0z" fill="none" />
              <g fill="currentColor">
                <path d="M4.715 6.542L3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199a2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z" />
                <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z" />
              </g>
            </svg>
          </button>
        </div>

        {/* MAIN CONTENT */}
        <div className="tpe-main-content">
          {/* Left Column */}
          <div className="tpe-left-column">
            {/* Profile Box */}
            <div className="tpe-profile-container">
              <div className="tpe-box-profile">
                <div className="tpe-box-pic" onDoubleClick={() => document.getElementById('tpeFileInput').click()}>
                  {profile?.profilePic?.url && (
                    <img
                      src={profile.profilePic.url}
                      alt="Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '15px' }}
                    />
                  )}
                  {uploading && (
                    <div style={{ position: 'absolute', zIndex: 3, background: 'rgba(0,0,0,0.5)', color: 'white', width: '100%', height: '100%', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Uploading...
                    </div>
                  )}
                </div>
                <input
                  id="tpeFileInput"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <div className="tpe-box-ttname">{getDisplayName()}</div>
                <div className="tpe-box-qual">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                      <path d="M14.217 3.5a5.17 5.17 0 0 0-4.434 0L3.092 6.637c-1.456.682-1.456 3.044 0 3.726l6.69 3.137a5.17 5.17 0 0 0 4.435 0l6.691-3.137c1.456-.682 1.456-3.044 0-3.726zM22 8.5v5" />
                      <path d="M5 11.5v5.125C5 19.543 9.694 21 12 21s7-1.457 7-4.375V11.5" />
                    </g>
                  </svg>
                  {getQualification()}
                </div>
                <div className="tpe-box-institution">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                    <path d="M0 0h256v256H0z" fill="none" />
                    <path fill="currentColor" d="M104 34H56a14 14 0 0 0-14 14v160a14 14 0 0 0 14 14h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14M54 78h52v100H54Zm2-32h48a2 2 0 0 1 2 2v18H54V48a2 2 0 0 1 2-2m48 164H56a2 2 0 0 1-2-2v-18h52v18a2 2 0 0 1-2 2m125.7-15L196.51 37.16a14 14 0 0 0-16.63-10.85l-46.81 10.06A14.09 14.09 0 0 0 122.3 53l33.19 157.81a14 14 0 0 0 6.1 8.9a13.85 13.85 0 0 0 7.57 2.26a13.6 13.6 0 0 0 3-.32l46.81-10.05A14.09 14.09 0 0 0 229.7 195m-82.81-83.32l50.73-10.9l14.12 67.16L161 178.81Zm-6.63-31.56L191 69.19L195.15 89l-50.73 10.9Zm-4.66-32l46.8-10.05a2 2 0 0 1 .42 0a1.9 1.9 0 0 1 1.05.32a2 2 0 0 1 .89 1.31l3.75 17.82l-50.72 10.82l-3.74-17.78a2.07 2.07 0 0 1 1.55-2.46Zm80.81 151.8L169.6 210a1.92 1.92 0 0 1-1.47-.27a2 2 0 0 1-.89-1.31l-3.75-17.82l50.72-10.9l3.79 17.73a2.07 2.07 0 0 1-1.59 2.47Z" />
                  </svg>
                  {getInstitution()}
                </div>
                <div className="tpe-box-bio">{profile?.bio || 'Bio goes here. Tell students about yourself.'}</div>
                <button className="tpe-edit-bio-btn" onClick={openBioModal} aria-label="Edit bio">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1.75em" height="1.75em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M3.548 20.938h16.9a.5.5 0 0 0 0-1h-16.9a.5.5 0 0 0 0 1M9.71 17.18a2.6 2.6 0 0 0 1.12-.65l9.54-9.54a1.75 1.75 0 0 0 0-2.47l-.94-.93a1.79 1.79 0 0 0-2.47 0l-9.54 9.53a2.5 2.5 0 0 0-.64 1.12L6.04 17a.74.74 0 0 0 .19.72a.77.77 0 0 0 .53.22Zm.41-1.36a1.47 1.47 0 0 1-.67.39l-.97.26l-1-1l.26-.97a1.5 1.5 0 0 1 .39-.67l.38-.37l1.99 1.99Zm1.09-1.08l-1.99-1.99l6.73-6.73l1.99 1.99Zm8.45-8.45L18.65 7.3l-1.99-1.99l1.01-1.02a.75.75 0 0 1 1.06 0l.93.94a.754.754 0 0 1 0 1.06" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Availability Box */}
            <div className="tpe-box-avail">
              <div className="tpe-avail-title">Availability</div>
              <div className="tpe-avail-content">
                <div className="tpe-days-grid">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div
                      key={day}
                      className={`tpe-day-btn ${selectedDays[day] ? 'active' : ''}`}
                      onClick={() => handleDayClick(day)}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </div>
                  ))}
                </div>
                <button className="tpe-save-avail-btn" onClick={saveAvailability}>Save Availability</button>
              </div>
              <div className="tpe-update-status" style={statusColor ? { color: statusColor } : {}}>{statusMessage}</div>
              <div className="tpe-selected-days-display">
                <strong>Selected days:</strong>{' '}
                {Object.entries(selectedDays).filter(([_, v]) => v).map(([k]) => k.charAt(0).toUpperCase() + k.slice(1)).join(', ') || 'None'}
              </div>
            </div>
          </div>

          {/* Right Column - Engagements */}
          <div className="tpe-engagements-box">
            <p className="tpe-eng-head">Engagements</p>
            <div className="tpe-eng-likes">Total likes: {totalLikes}</div>
          </div>
        </div>
      </div>

      {/* SHARE PROFILE MODAL */}
      {isShareModalOpen && (
        <div className="tpe-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget) closeShareModal(); }}>
          <div className="tpe-modal">
            <button className="tpe-modal-close" onClick={closeShareModal}>✕</button>
            <div className="tpe-modal-icon-ring tpe-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="currentColor" d="M9.745 21.745C5.308 20.722 2 16.747 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10c0 4.747-3.308 8.722-7.745 9.745L12 24zm-2.733-3.488a7.95 7.95 0 0 0 3.182 1.539l.56.129L12 21.172l1.247-1.247l.56-.13a7.96 7.96 0 0 0 3.36-1.686A6.98 6.98 0 0 0 12.16 16c-2.036 0-3.87.87-5.148 2.257M5.616 16.82A8.98 8.98 0 0 1 12.16 14a8.97 8.97 0 0 1 6.362 2.634a8 8 0 1 0-12.906.187M12 13a4 4 0 1 1 0-8a4 4 0 0 1 0 8m0-2a2 2 0 1 0 0-4a2 2 0 0 0 0 4" />
              </svg>
            </div>
            <div className="tpe-modal-title">Share profile</div>
            <div className="tpe-modal-sub">Get discovered outside the platform with a link to your profile.</div>
            <div className="tpe-share-link-field">
              <span className="tpe-share-link-text">https://mykaddle.com/{profile?.slug || 'tutor'}</span>
            </div>
            <div className="tpe-modal-actions">
              <button className="tpe-btn-cancel" onClick={closeShareModal}>Cancel</button>
              <button className="tpe-btn-confirm-green" onClick={copyProfileLink}>Copy link</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT BIO MODAL */}
      {isBioModalOpen && (
        <div className="tpe-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget) closeBioModal(); }}>
          <div className="tpe-modal">
            <button className="tpe-modal-close" onClick={closeBioModal}>✕</button>
            <div className="tpe-modal-icon-ring tpe-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="currentColor" d="M3.548 20.938h16.9a.5.5 0 0 0 0-1h-16.9a.5.5 0 0 0 0 1M9.71 17.18a2.6 2.6 0 0 0 1.12-.65l9.54-9.54a1.75 1.75 0 0 0 0-2.47l-.94-.93a1.79 1.79 0 0 0-2.47 0l-9.54 9.53a2.5 2.5 0 0 0-.64 1.12L6.04 17a.74.74 0 0 0 .19.72a.77.77 0 0 0 .53.22Zm.41-1.36a1.47 1.47 0 0 1-.67.39l-.97.26l-1-1l.26-.97a1.5 1.5 0 0 1 .39-.67l.38-.37l1.99 1.99Zm1.09-1.08l-1.99-1.99l6.73-6.73l1.99 1.99Zm8.45-8.45L18.65 7.3l-1.99-1.99l1.01-1.02a.75.75 0 0 1 1.06 0l.93.94a.754.754 0 0 1 0 1.06" />
              </svg>
            </div>
            <div className="tpe-modal-title">Update Bio</div>
            <div className="tpe-modal-sub">Tell students about yourself, your experience, and your teaching approach.</div>
            <textarea
              className="tpe-bio-textarea"
              maxLength={MAX_BIO_CHARS}
              placeholder="Write your bio…"
              value={bioText}
              onChange={(e) => setBioText(e.target.value)}
            />
            <div className={`tpe-bio-char-count ${getBioCharCount() >= 460 ? 'warn' : ''}`}>
              {getBioCharCount()} / {MAX_BIO_CHARS}
            </div>
            <div className="tpe-modal-actions">
              <span className="tpe-modal-footer-hint">*To update profile picture double tap on the item</span>
              <button className="tpe-btn-cancel" onClick={closeBioModal}>Cancel</button>
              <button className="tpe-btn-confirm-green" onClick={saveBio}>Save Bio</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorProfileEdit;